import { useState } from "react";
import PropTypes from "prop-types";
import { formatCurrency, formatDateTime } from "../../utils/utils";
import {
  HISTORY_ACTION,
  HISTORY_ACTION_LABELS,
} from "../../constants/disputes";
import "./DisputeHistory.css";

// Colored left border per action, matching the Figma timeline.
const ACTION_ACCENT = {
  [HISTORY_ACTION.DISPUTE_SUBMITTED]: "dispute-history-entry--submitted",
  [HISTORY_ACTION.APPEAL_SUBMITTED]: "dispute-history-entry--submitted",
  [HISTORY_ACTION.DISPUTE_DECLINED]: "dispute-history-entry--declined",
  [HISTORY_ACTION.DISPUTE_SETTLED]: "dispute-history-entry--settled",
};

const isReasonShown = (action) => action !== HISTORY_ACTION.DISPUTE_SETTLED;

const HistoryEntry = ({ entry, currency, onDownloadAttachment }) => {
  const [expanded, setExpanded] = useState(false);

  const actionLabel = HISTORY_ACTION_LABELS[entry.action] || entry.action;
  const hasExpandable =
    entry.issueDescription ||
    (entry.attachments && entry.attachments.length > 0) ||
    entry.latestTransactedBookingAmount !== undefined ||
    entry.latestPlatformFee !== undefined;

  return (
    <div
      className={`dispute-history-entry ${ACTION_ACCENT[entry.action] || ""}`}
    >
      <div className="dispute-history-entry-head">
        <span className="dispute-history-action">{actionLabel}</span>
        <span className="dispute-history-date">
          {formatDateTime(entry.date)}
        </span>
      </div>

      {isReasonShown(entry.action) && entry.reason && (
        <div className="dispute-history-reason">
          <span className="dispute-history-reason-label">Reason:</span>{" "}
          {entry.reason}
        </div>
      )}

      <div className="dispute-history-meta">
        {entry.disputedBookingAmount !== undefined &&
          entry.disputedBookingAmount !== null && (
            <span>
              Disputed amount:{" "}
              <strong>
                {formatCurrency(entry.disputedBookingAmount, currency)}
              </strong>
            </span>
          )}
        {entry.submittedBy && (
          <span className="dispute-history-by">By: {entry.submittedBy}</span>
        )}
      </div>

      {hasExpandable && (
        <>
          <button
            type="button"
            className="dispute-history-toggle"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "Hide details" : "Show details"}
          </button>

          {expanded && (
            <div className="dispute-history-details">
              {entry.issueDescription && (
                <div className="dispute-history-detail-row">
                  <span className="dispute-history-detail-label">
                    Issue Description
                  </span>
                  <span className="dispute-history-detail-value">
                    {entry.issueDescription}
                  </span>
                </div>
              )}
              {entry.attachments && entry.attachments.length > 0 && (
                <div className="dispute-history-detail-row">
                  <span className="dispute-history-detail-label">
                    Attachments
                  </span>
                  <span className="dispute-history-attachments">
                    {entry.attachments.map((att) => (
                      <button
                        key={att.attachmentId}
                        type="button"
                        className="dispute-history-attachment"
                        onClick={() => onDownloadAttachment?.(att)}
                      >
                        {att.fileName}
                      </button>
                    ))}
                  </span>
                </div>
              )}
              {entry.latestTransactedBookingAmount !== undefined &&
                entry.latestTransactedBookingAmount !== null && (
                  <div className="dispute-history-detail-row">
                    <span className="dispute-history-detail-label">
                      Latest Transacted Booking Amount
                    </span>
                    <span className="dispute-history-detail-value">
                      {formatCurrency(
                        entry.latestTransactedBookingAmount,
                        currency
                      )}
                    </span>
                  </div>
                )}
              {entry.latestPlatformFee !== undefined &&
                entry.latestPlatformFee !== null && (
                  <div className="dispute-history-detail-row">
                    <span className="dispute-history-detail-label">
                      Latest Platform Fee
                    </span>
                    <span className="dispute-history-detail-value">
                      {formatCurrency(entry.latestPlatformFee, currency)}
                    </span>
                  </div>
                )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

HistoryEntry.propTypes = {
  entry: PropTypes.object.isRequired,
  currency: PropTypes.string,
  onDownloadAttachment: PropTypes.func,
};

const DisputeHistory = ({ history = [], currency, onDownloadAttachment }) => {
  if (!history.length) {
    return <div className="dispute-history-empty">No history yet.</div>;
  }

  // Most recent first.
  const ordered = [...history].reverse();

  return (
    <div className="dispute-history">
      {ordered.map((entry, index) => (
        <HistoryEntry
          key={index}
          entry={entry}
          currency={currency}
          onDownloadAttachment={onDownloadAttachment}
        />
      ))}
    </div>
  );
};

DisputeHistory.propTypes = {
  history: PropTypes.arrayOf(PropTypes.object),
  currency: PropTypes.string,
  onDownloadAttachment: PropTypes.func,
};

export default DisputeHistory;
